import { Alert, TextField } from '@mui/material';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from './Dialogs/FormDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { Tenant } from '../../core/types/data-models';

interface NewTenantDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the newly created tenant after a successful POST. */
  onCreated?: (tenant: Tenant) => void;
}

/** Lightweight tenant-creation dialog used by the sidebar's "New tenant" item. */
export function NewTenantDialog({ open, onClose, onCreated }: NewTenantDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (payload: Partial<Tenant>) => resources.tenants.create(payload),
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      onCreated?.(tenant);
      setName('');
      onClose();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      await create.mutateAsync({ name: name.trim() });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <FormDialog
      open={open}
      title="New tenant"
      submitLabel="Create"
      loading={create.isPending}
      onClose={() => {
        setName('');
        setError(null);
        onClose();
      }}
      onSubmit={handleSubmit}
    >
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label="Tenant name"
        required
        autoFocus
        fullWidth
        value={name}
        onChange={(e) => setName(e.target.value)}
        helperText="Display name. You can rename it later from the Tenants page."
      />
    </FormDialog>
  );
}
